/* Store locator (sections/os-store-locator.liquid). A port of the custom Target
   locator on shop.theonlybean.com for every retailer we stock: MapLibre on
   OpenFreeMap tiles recoloured to the brand, clustered pins, a zip or address
   search, the nearest stores as cards. Everything loads lazily as the section
   nears the viewport. Contract: [data-os-locator] with data-src (stores JSON),
   data-shop-<retailer> links and data-nearest; children carry the data-os-locator-* hooks. */
(() => {
  const MAPLIBRE = 'https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl';
  const TILES = 'https://tiles.openfreemap.org/styles/positron';
  const ERR = 'We could not find that location. Try a 5-digit zip.';
  const R_MI = 3958.8;
  const toRad = (d) => (d * Math.PI) / 180;
  const miles = (a, b) => {
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * R_MI * Math.asin(Math.sqrt(h));
  };
  const geocode = async (q) => {
    if (/^\d{5}$/.test(q)) {
      const r = await fetch('https://api.zippopotam.us/us/' + q);
      if (!r.ok) return null;
      const p = (await r.json()).places?.[0];
      return p ? { lat: +p.latitude, lng: +p.longitude } : null;
    }
    const r = await fetch('https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=us&q=' + encodeURIComponent(q));
    if (!r.ok) return null;
    const j = await r.json();
    return j?.[0] ? { lat: +j[0].lat, lng: +j[0].lon } : null;
  };
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  const init = (root) => {
    if (root.dataset.osLocatorReady) return;
    root.dataset.osLocatorReady = 'true';
    const mapEl = root.querySelector('[data-os-locator-map]');
    const listEl = root.querySelector('[data-os-locator-list]');
    const form = root.querySelector('[data-os-locator-form]');
    const input = root.querySelector('[data-os-locator-input]');
    const btn = root.querySelector('[data-os-locator-btn]');
    const msg = root.querySelector('[data-os-locator-msg]');
    const resultsStatus = root.querySelector('[data-os-locator-results-status]');
    const chips = [...root.querySelectorAll('[data-os-locator-retailer]')];
    const nearest = Number(root.dataset.nearest) || 10;
    const shopLinks = { target: root.dataset.shopTarget, walmart: root.dataset.shopWalmart };
    const prompt = listEl.innerHTML;
    let stores = [];
    let names = {};
    let map = null;
    let popup = null;
    let origin = null;
    let searchVersion = 0;
    let announcementTimer;
    let programmatic = false;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const clearAnnouncement = () => {
      clearTimeout(announcementTimer);
      if (resultsStatus) resultsStatus.textContent = '';
    };
    const announceResults = (count, tally = false) => {
      clearAnnouncement();
      const template = tally ? root.dataset.filterStatus || 'Filters updated: [count] stores match.'
        : root.dataset.resultsStatus || 'Showing [count] nearby stores.';
      // Separate mutations let repeated counts be announced; rapid updates coalesce.
      announcementTimer = setTimeout(() => {
        if (resultsStatus) resultsStatus.textContent = template.replace('[count]', count.toLocaleString());
      }, 100);
    };

    const active = () => new Set(chips.filter((c) => c.checked).map((c) => c.value));
    const visible = () => { const on = active(); return stores.filter((s) => on.has(s.key)); };
    const addr = (s) => `${s.street}, ${s.city}, ${s.state}${s.zip ? ' ' + s.zip : ''}`;
    const dirUrl = (s) => 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(addr(s));
    const iconUrl = (key) => `${root.dataset.logoBase || ''}os-retailer-icon-${key}.webp`;
    const pinHtml = (s) => `<img class="os-locator__pin" src="${esc(iconUrl(s.key))}" alt="" width="32" height="32" loading="lazy">`;
    const markerImage = (image) => {
      const canvas = document.createElement('canvas');
      canvas.width = 80; canvas.height = 96;
      const ctx = canvas.getContext('2d');
      ctx.scale(2, 2);
      ctx.fillStyle = '#fff'; ctx.strokeStyle = '#bcbcb6'; ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(10, .5); ctx.lineTo(30, .5); ctx.quadraticCurveTo(39.5, .5, 39.5, 10);
      ctx.lineTo(39.5, 30); ctx.quadraticCurveTo(39.5, 39.5, 30, 39.5);
      ctx.lineTo(25, 39.5); ctx.lineTo(20, 46); ctx.lineTo(15, 39.5); ctx.lineTo(10, 39.5);
      ctx.quadraticCurveTo(.5, 39.5, .5, 30); ctx.lineTo(.5, 10); ctx.quadraticCurveTo(.5, .5, 10, .5);
      ctx.closePath(); ctx.fill(); ctx.stroke();
      if (image) {
        ctx.save(); ctx.beginPath(); ctx.roundRect(4, 4, 32, 32, 7); ctx.clip();
        ctx.drawImage(image, 4, 4, 32, 32); ctx.restore();
      } else {
        ctx.beginPath(); ctx.arc(20, 20, 6, 0, Math.PI * 2); ctx.fillStyle = '#111'; ctx.fill();
      }
      return ctx.getImageData(0, 0, 80, 96);
    };
    const linksHtml = (s) => {
      const shop = shopLinks[s.key];
      return `<div class="os-locator__links">${shop ? `<a class="os-locator__shop" href="${esc(shop)}" target="_blank" rel="noopener">Shop online</a>` : ''}<a class="os-locator__dir" href="${dirUrl(s)}" target="_blank" rel="noopener">Directions</a></div>`;
    };
    const cardHtml = (s, mi) => `${pinHtml(s)}<div class="os-locator__card-body"><p class="os-locator__card-name" role="button" tabindex="0" data-os-locator-select aria-label="${esc(`Show ${names[s.key] || s.key} at ${addr(s)} on map`)}">${esc(names[s.key] || s.key)}${mi != null ? ` <span class="os-locator__mi">${mi.toFixed(1)} mi</span>` : ''}</p><p class="os-locator__card-addr">${esc(addr(s))}</p>${linksHtml(s)}</div>`;

    // Empty state: one row per retailer with its store count, under the prompt.
    const renderTally = () => {
      if (origin || !stores.length) return;
      const counts = {};
      visible().forEach((s) => { counts[s.key] = (counts[s.key] || 0) + 1; });
      const base = root.dataset.logoBase || '';
      const tallyLogo = (key) => key === 'target' ? root.dataset.targetLogo : key === 'walmart' ? root.dataset.walmartLogo : `${base}os-store-${key}.svg`;
      listEl.innerHTML = prompt + Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([key, n]) =>
        `<div class="os-locator__tally"><img src="${esc(tallyLogo(key))}" alt="" loading="lazy"><div><p class="os-locator__tally-name">${esc(names[key] || key)}</p><p class="os-locator__tally-count">${n.toLocaleString()} stores</p></div></div>`).join('');
    };
    const renderList = (items) => {
      listEl.innerHTML = '';
      items.forEach(({ store, mi }) => {
        const card = document.createElement('div');
        card.className = 'os-locator__card';
        card.dataset.id = store.id;
        card.innerHTML = cardHtml(store, mi);
        listEl.appendChild(card);
      });
    };
    const setActive = (id) => {
      listEl.querySelectorAll('.os-locator__card').forEach((c) => {
        const on = c.dataset.id === id;
        c.classList.toggle('is-active', on);
        if (on) c.scrollIntoView({ block: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
      });
    };
    const nearestTo = (set, from, count, withMiles) => set
      .map((s) => ({ store: s, mi: miles(from, s) }))
      .sort((a, b) => a.mi - b.mi)
      .slice(0, count)
      .map((it) => ({ store: it.store, mi: withMiles ? it.mi : null }));

    // ---- map ----
    const byId = (id) => stores.find((s) => s.id === id);
    const move = (fn) => { programmatic = true; fn(); map.once('idle', () => { programmatic = false; }); };
    // Replacing a popup must not restore stale focus over a search/filter action.
    const dismissPopup = () => {
      const previous = popup;
      popup = null;
      previous?.remove();
    };
    const openPopup = (s, opener = document.activeElement, keyboard = false) => {
      const lib = window.maplibregl;
      if (!map || !lib) return;
      dismissPopup();
      popup = new lib.Popup({ offset: 46, focusAfterOpen: false }).setLngLat([s.lng, s.lat])
        .setHTML(`<p class="os-locator__card-name">${esc(names[s.key] || s.key)}</p><p class="os-locator__card-addr">${esc(addr(s))}</p>${linksHtml(s)}`)
        .addTo(map);
      const current = popup;
      const element = current.getElement();
      let hadFocus = false;
      let explicitClose = false;
      element.addEventListener('focusin', () => { hadFocus = true; });
      element.addEventListener('focusout', (e) => {
        // Removal may blur to BODY before MapLibre emits its close event.
        if (e.relatedTarget && !element.contains(e.relatedTarget)) hadFocus = false;
      });
      element.addEventListener('click', (e) => {
        if (e.target.closest('.maplibregl-popup-close-button')) explicitClose = true;
      }, true);
      element.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        e.preventDefault(); e.stopPropagation();
        explicitClose = true;
        current.remove();
      });
      current.on('close', () => {
        if (popup !== current) return;
        popup = null;
        if (!hadFocus && !explicitClose) return;
        const returnTo = opener?.isConnected && opener !== document.body ? opener
          : listEl.querySelector(`[data-id="${s.id}"] [data-os-locator-select]`) || input;
        returnTo.focus({ preventScroll: true });
      });
      if (keyboard) element.querySelector('a, button')?.focus({ preventScroll: true });
    };
    const focusStore = (id, withPopup, opener, keyboard = false) => {
      const s = byId(id);
      if (!map || !s) return;
      move(() => map.easeTo({ center: [s.lng, s.lat], zoom: Math.max(map.getZoom(), 10) }));
      if (withPopup) openPopup(s, opener, keyboard);
    };
    const geojson = () => ({
      type: 'FeatureCollection',
      features: visible().map((s) => ({ type: 'Feature', geometry: { type: 'Point', coordinates: [s.lng, s.lat] }, properties: { id: s.id, key: s.key } })),
    });
    const recolor = () => {
      (map.getStyle().layers || []).forEach((ly) => {
        const id = ly.id.toLowerCase();
        try {
          if (ly.type === 'background') map.setPaintProperty(ly.id, 'background-color', '#F2EDE4');
          else if (ly.type === 'fill') {
            if (/water|ocean/.test(id)) map.setPaintProperty(ly.id, 'fill-color', '#7CDBEA');
            else if (/landcover|park|wood|grass|forest|green|nature/.test(id)) { map.setPaintProperty(ly.id, 'fill-color', '#B4E8CD'); map.setLayerZoomRange(ly.id, 0, 24); }
            else if (/landuse|residential|sand|beach/.test(id)) map.setPaintProperty(ly.id, 'fill-color', '#EFE9DF');
            else if (/building/.test(id)) map.setPaintProperty(ly.id, 'fill-color', '#E7E1D6');
          } else if (ly.type === 'line') {
            if (/boundary|admin/.test(id)) { map.setPaintProperty(ly.id, 'line-color', '#7d7d7d'); map.setPaintProperty(ly.id, 'line-dasharray', [2.5, 2]); }
            else if (/waterway|river/.test(id)) map.setPaintProperty(ly.id, 'line-color', '#7CDBEA');
            else if (/highway|railway|aeroway|road|tunnel|pier/.test(id)) map.setLayerZoomRange(ly.id, Math.max(ly.minzoom || 0, 7), 24);
          } else if (ly.type === 'symbol') {
            map.setPaintProperty(ly.id, 'text-color', '#4A4A4A');
            map.setPaintProperty(ly.id, 'text-halo-color', 'rgba(255,255,255,.85)');
          }
        } catch (e) { /* layer names vary by style version; best effort */ }
      });
    };
    const syncListToStores = (set, ref) => {
      if (!set.length) return;
      const from = origin || ref;
      renderList(nearestTo(set, from, 12, !!origin));
    };
    const syncListToView = () => {
      if (!map || !stores.length) return;
      const b = map.getBounds();
      const inView = visible().filter((s) => s.lng >= b.getWest() && s.lng <= b.getEast() && s.lat >= b.getSouth() && s.lat <= b.getNorth());
      const c = map.getCenter();
      syncListToStores(inView, { lat: c.lat, lng: c.lng });
    };
    const addLayers = () => {
      map.addSource('os-stores', { type: 'geojson', data: geojson(), cluster: true, clusterMaxZoom: 9, clusterRadius: 44 });
      map.addLayer({ id: 'os-clusters', type: 'circle', source: 'os-stores', filter: ['has', 'point_count'],
        paint: { 'circle-color': '#000', 'circle-stroke-color': '#fff', 'circle-stroke-width': 2.5, 'circle-radius': ['step', ['get', 'point_count'], 14, 25, 18, 100, 24] } });
      map.addLayer({ id: 'os-cluster-count', type: 'symbol', source: 'os-stores', filter: ['has', 'point_count'],
        layout: { 'text-field': ['get', 'point_count_abbreviated'], 'text-font': ['Noto Sans Bold'], 'text-size': 13 }, paint: { 'text-color': '#fff' } });
      map.addImage('os-retailer-fallback', markerImage(), { pixelRatio: 2 });
      map.addLayer({ id: 'os-store-outer', type: 'symbol', source: 'os-stores', filter: ['!', ['has', 'point_count']],
        layout: { 'icon-image': ['coalesce', ['image', ['concat', 'os-retailer-', ['get', 'key']]], ['image', 'os-retailer-fallback']],
          'icon-size': 1, 'icon-anchor': 'bottom', 'icon-allow-overlap': true, 'icon-ignore-placement': true } });
      [...new Set(stores.map((s) => s.key))].forEach((key) => {
        map.loadImage(iconUrl(key)).then(({ data }) => {
          map.addImage('os-retailer-' + key, markerImage(data), { pixelRatio: 2 });
        }).catch(() => {});
      });
      map.on('click', 'os-clusters', (e) => {
        const f = map.queryRenderedFeatures(e.point, { layers: ['os-clusters'] })[0];
        Promise.resolve(map.getSource('os-stores').getClusterLeaves(f.properties.cluster_id, 10000, 0)).then((leaves) => {
          if (!leaves?.length) return;
          const b = new window.maplibregl.LngLatBounds();
          leaves.forEach((lf) => b.extend(lf.geometry.coordinates));
          move(() => map.fitBounds(b, { padding: 70, animate: !reduceMotion, maxZoom: 11.5 }));
          syncListToStores(leaves.map((lf) => byId(String(lf.properties.id))).filter(Boolean), { lat: (b.getSouth() + b.getNorth()) / 2, lng: (b.getWest() + b.getEast()) / 2 });
        }).catch(() => {});
      });
      map.on('click', 'os-store-outer', (e) => {
        const s = e.features?.[0] && byId(String(e.features[0].properties.id));
        if (!s) return;
        openPopup(s);
        if (!listEl.querySelector(`.os-locator__card[data-id="${s.id}"]`)) syncListToView();
        setActive(s.id);
      });
      map.on('moveend', () => { if (!programmatic && map.getZoom() >= 7.5) syncListToView(); });
      ['os-clusters', 'os-store-outer'].forEach((layer) => {
        map.on('mouseenter', layer, () => { map.getCanvas().style.cursor = 'pointer'; });
        map.on('mouseleave', layer, () => { map.getCanvas().style.cursor = ''; });
      });
    };
    const hideMap = () => { mapEl.hidden = true; root.querySelector('.os-locator__grid').style.gridTemplateColumns = '1fr'; };
    const initMap = () => {
      const lib = window.maplibregl;
      mapEl.querySelector('.os-locator__map-fallback')?.remove();
      map = new lib.Map({ container: mapEl, style: TILES, center: [-96, 38], zoom: 3.2, scrollZoom: false, attributionControl: { compact: true } });
      map.addControl(new lib.NavigationControl({ showCompass: false }), 'top-left');
      map.on('error', () => { if (!map.osLoaded) hideMap(); });
      map.on('load', () => {
        map.osLoaded = true;
        recolor();
        if (stores.length) addLayers();
        else map.once('idle', () => stores.length && !map.getSource('os-stores') && addLayers());
        if (origin) showResults(origin, false);
      });
    };

    // ---- boot ----
    const loadStores = () => fetch(root.dataset.src).then((r) => { if (!r.ok) throw new Error('stores'); return r.json(); }).then((j) => {
      names = j.retailers || {};
      const f = j.fields;
      stores = (j.stores || []).map((row, i) => {
        const s = {}; f.forEach((k, n) => { s[k] = row[n]; }); s.id = String(i); return s;
      });
      if (map?.osLoaded && !map.getSource('os-stores')) addLayers();
      renderTally();
    }).catch(() => {});
    const loadMap = () => {
      const css = document.createElement('link'); css.rel = 'stylesheet'; css.href = MAPLIBRE + '.css'; document.head.appendChild(css);
      const js = document.createElement('script'); js.src = MAPLIBRE + '.js';
      js.onload = () => { try { initMap(); } catch (e) { hideMap(); } };
      js.onerror = hideMap;
      document.head.appendChild(js);
    };
    let booted = false;
    const boot = () => { if (booted) return; booted = true; loadStores(); loadMap(); };
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => entries.forEach((en) => { if (en.isIntersecting) { io.disconnect(); boot(); } }), { rootMargin: '400px' });
      io.observe(root);
    } else boot();

    // ---- search ----
    const showMsg = (t) => { msg.textContent = t; };
    const locationStatus = root.querySelector('[data-os-locator-location-status]');
    const announceLocation = (t) => { if (locationStatus) locationStatus.textContent = t; };
    const showResults = (at, interactive = true) => {
      clearAnnouncement();
      origin = at;
      const set = visible();
      if (!set.length) {
        renderList([]);
        dismissPopup();
        showMsg(root.dataset.noRetailers);
        return;
      }
      const ranked = nearestTo(set, at, nearest, true);
      renderList(ranked);
      if (map?.osLoaded) {
        const b = new window.maplibregl.LngLatBounds();
        ranked.slice(0, 3).forEach((it) => b.extend([it.store.lng, it.store.lat]));
        b.extend([at.lng, at.lat]);
        move(() => map.fitBounds(b, { padding: 60, animate: !reduceMotion, maxZoom: 11 }));
      }
      if (interactive) {
        setActive(ranked[0].store.id);
        focusStore(ranked[0].store.id, true);
        announceResults(ranked.length);
      }
    };
    const runSearch = async () => {
      const q = input.value.trim();
      if (!q) return;
      const version = ++searchVersion;
      showMsg('');
      announceLocation('');
      clearAnnouncement();
      boot();
      btn.disabled = true;
      try {
        if (!stores.length) await loadStores();
        const at = await geocode(q);
        if (version !== searchVersion) return;
        btn.disabled = false;
        const set = visible();
        if (!at || !set.length) { showMsg(set.length ? ERR : 'Tick at least one retailer.'); return; }
        showResults(at);
      } catch (e) {
        if (version !== searchVersion) return;
        btn.disabled = false;
        showMsg(ERR);
      }
    };
    form.addEventListener('submit', (e) => { e.preventDefault(); runSearch(); });
    input.addEventListener('input', () => {
      searchVersion++;
      clearAnnouncement();
      btn.disabled = false;
      showMsg('');
      announceLocation('');
    });

    const requestLocation = () => {
      const version = ++searchVersion;
      btn.disabled = false;
      try { sessionStorage.setItem('os-locator-location-requested-v1', '1'); } catch (e) {}
      announceLocation(root.dataset.locating);
      boot();
      const failed = (error) => {
        if (version !== searchVersion) return;
        announceLocation(error?.code === 1 ? root.dataset.locationDenied : root.dataset.locationFailed);
      };
      try {
        navigator.geolocation.getCurrentPosition(async ({ coords }) => {
          if (version !== searchVersion) return;
          if (!Number.isFinite(coords.latitude) || !Number.isFinite(coords.longitude) || Math.abs(coords.latitude) > 90 || Math.abs(coords.longitude) > 180) { failed(); return; }
          if (!stores.length) await loadStores();
          if (version !== searchVersion) return;
          if (!stores.length) { failed(); return; }
          announceLocation(root.dataset.locationFound);
          input.value = '';
          showResults({ lat: coords.latitude, lng: coords.longitude }, false);
        }, failed, { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 });
      } catch (e) { failed(e); }
    };
    if (window.isSecureContext && navigator.geolocation) {
      let attempted = false;
      try { attempted = sessionStorage.getItem('os-locator-location-requested-v1') === '1'; } catch (e) {}
      if (!attempted && !input.value.trim() && !window.Shopify?.designMode) requestLocation();
    }

    // ---- retailer chips ----
    chips.forEach((c) => c.addEventListener('change', () => {
      if (map?.getSource('os-stores')) map.getSource('os-stores').setData(geojson());
      if (origin) { showMsg(''); showResults(origin); } else if (map?.osLoaded && map.getZoom() >= 7.5) syncListToView(); else renderTally();
      if (!origin) announceResults(visible().length, true);
    }));

    // ---- card <-> pin ----
    const activate = (card, keyboard = false) => {
      setActive(card.dataset.id);
      focusStore(card.dataset.id, true, card.querySelector('[data-os-locator-select]'), keyboard);
    };
    listEl.addEventListener('click', (e) => {
      if (e.target.closest('a')) return;
      const card = e.target.closest('.os-locator__card');
      if (card) activate(card);
    });
    listEl.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      if (!e.target.closest('[data-os-locator-select]')) return;
      const card = e.target.closest('.os-locator__card');
      if (card) { e.preventDefault(); activate(card, true); }
    });
  };

  const initAll = (scope = document) => scope.querySelectorAll('[data-os-locator]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
})();
